/**
 * BrandingContext — runtime brand theming.
 *
 * Responsibilities:
 *   1. After a user logs in, fetch GET /api/companies/me/branding (one call,
 *      cached in this context).
 *   2. Apply the company's primaryColor to `:root` as CSS custom properties:
 *        --brand-primary       e.g. #3194A0
 *        --brand-primary-dark  e.g. #1F6066 (35% mixed with black)
 *        --brand-primary-soft  e.g. #DEF0F2 (mostly mixed with white)
 *        --brand-primary-fg    e.g. #FFFFFF (text color for use on brand bg)
 *      These are read by the Tailwind theme block in `index.css`, which
 *      exposes `bg-brand`, `text-brand`, `border-brand`, `bg-brand-soft`,
 *      `text-brand-fg`. Components opt in to brand color by using these
 *      classes instead of hard-coded `bg-teal-700`.
 *   3. Expose `{ branding, refresh }` so the Settings page can refresh after
 *      a successful color/logo update.
 *
 * Safety: when the user is a super_admin or unauthenticated, branding is
 * the default (null fields). The default brand color is #3194A0 — same as
 * the prior hardcoded teal.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import { api, tokens } from "@/lib/api-client";
import type { CompanyBranding } from "@/types/api";
import { useAuth } from "./AuthContext";

const DEFAULT_BRAND = "#3194A0";

interface BrandingState {
  branding: CompanyBranding;
  /** Re-fetch branding (call after the Settings page saves). */
  refresh: () => Promise<void>;
}

const defaultBranding: CompanyBranding = {
  companyId: null,
  companyName: null,
  primaryColor: null,
  logoUrl: null,
};

const BrandingContext = createContext<BrandingState>({
  branding: defaultBranding,
  refresh: async () => undefined,
});

/** WCAG-ish luminance for choosing white-vs-black foreground on the brand bg. */
function luminance(hex: string): number {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function normalizeHex(s: string | null | undefined): string | null {
  if (!s) return null;
  const stripped = s.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(stripped)) return null;
  return "#" + stripped.toUpperCase();
}

/** Convert hex (#RRGGBB) to HSL space-separated triplet "H S% L%". */
function hexToHslTriplet(hex: string): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let H = 0, S = 0;
  const L = (max + min) / 2;
  if (d !== 0) {
    S = d / (1 - Math.abs(2 * L - 1));
    switch (max) {
      case r: H = ((g - b) / d) % 6; break;
      case g: H = (b - r) / d + 2; break;
      default: H = (r - g) / d + 4;
    }
    H = H * 60;
    if (H < 0) H += 360;
  }
  return `${Math.round(H)} ${Math.round(S * 100)}% ${Math.round(L * 100)}%`;
}

/** Apply brand variables to :root so every existing `bg-primary`,
 *  `text-primary`, `border-primary` etc. picks up the company color
 *  without any component-level changes. */
function applyCssVariables(primary: string | null): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const hex = normalizeHex(primary) ?? DEFAULT_BRAND;

  // HSL components for the existing token system (hsl(var(--primary))).
  // We tweak hover and active by ±6% lightness so buttons keep their feel.
  const triplet = hexToHslTriplet(hex);
  const [hStr, sStr, lStr] = triplet.split(" ");
  const lNum = parseInt((lStr ?? "0").replace("%", ""), 10);
  const sNum = parseInt((sStr ?? "0").replace("%", ""), 10);
  const hover = `${hStr} ${Math.min(100, sNum + 2)}% ${Math.max(0, lNum - 6)}%`;
  const active = `${hStr} ${Math.min(100, sNum + 7)}% ${Math.max(0, lNum - 12)}%`;
  const isDark = root.getAttribute("data-theme") === "dark";
  // primary-soft = subtle background fill, and the text that sits on it.
  //
  // These MUST track the theme. Inline properties win over the stylesheet, so
  // setting a light tint unconditionally masked `[data-theme="dark"]`'s
  // --primary-soft while leaving --primary-soft-fg on the dark stylesheet
  // value — a near-white chip carrying near-white text. The inspector chips in
  // the new-inspection wizard were unreadable in dark mode because of it.
  //
  // Same recipe as --ai-soft below. The lightness/saturation pairs reproduce
  // the stylesheet's own tokens for the default brand, so nothing shifts for a
  // company that hasn't set a colour.
  const soft   = isDark ? `${hStr} 40% 20%` : `${hStr} 60% 95%`;
  const softFg = isDark ? `${hStr} 60% 80%` : `${hStr} 56% 27%`;
  // Pick FG for contrast on top of the brand.
  const lum = luminance(hex);
  const fg = lum > 0.5 ? "222 47% 11%" : "0 0% 100%";

  root.style.setProperty("--primary", triplet);
  root.style.setProperty("--primary-hover", hover);
  root.style.setProperty("--primary-active", active);
  root.style.setProperty("--primary-fg", fg);
  root.style.setProperty("--primary-soft", soft);
  root.style.setProperty("--primary-soft-fg", softFg);
  // Focus rings (input/button/select/dialog/etc.) read from --ring. Tie
  // it to the brand triplet so the focus halo always matches the inner
  // focus-border instead of staying on the stylesheet default — that
  // mismatch is what produced the "two colors on focus" effect.
  root.style.setProperty("--ring", triplet);

  // AI accent tokens used by surfaces like the "Ask AI" button
  // (border, soft tint background, soft text). The stylesheet ships
  // different values for light vs dark; mirror that recipe with the
  // brand's hue/saturation so the button tracks the user's theme in
  // both modes instead of staying on the stylesheet's teal default.
  const aiSoft   = isDark ? `${hStr} 50% 16%` : `${hStr} 60% 96%`;
  const aiSoftFg = isDark ? `${hStr} 75% 78%` : `${hStr} 65% 22%`;
  const aiBorder = isDark ? `${hStr} 45% 32%` : `${hStr} 50% 70%`;
  root.style.setProperty("--ai-soft",     aiSoft);
  root.style.setProperty("--ai-soft-fg",  aiSoftFg);
  root.style.setProperty("--ai-border",   aiBorder);

  // Sidebar accent — the rail's active-item highlight (active pill fill +
  // inset border, active icon, left edge rail, and the soft wash at the top
  // of `.bg-sidebar-surface`). It must track the company color too, so it is
  // derived from the same brand hue/saturation rather than staying on the
  // stylesheet's teal. The rail is dark in both themes, so lightness is
  // nudged up off the brand's own lightness (and clamped) to stay legible on
  // it: the default #3194A0 (187 53% 41%) reproduces the stylesheet values
  // exactly — 187 53% 45% in light, 187 53% 50% in dark.
  const sidebarL = isDark
    ? Math.max(48, Math.min(78, lNum + 9))
    : Math.max(42, Math.min(70, lNum + 4));
  root.style.setProperty("--sidebar-accent", `${hStr} ${sNum}% ${sidebarL}%`);

  // AI gradient — keep some visual motion by walking the hue/lightness a
  // touch between the two stops. `from` sits near the brand's lightness;
  // `to` is a few degrees up the wheel and slightly lighter so the
  // gradient still feels like a gradient rather than a flat block.
  const hNum = parseInt(hStr ?? "0", 10);
  const aiFromL = Math.max(20, Math.min(70, lNum + 4));
  const aiToL   = Math.max(28, Math.min(78, lNum + 12));
  const aiToH   = (hNum + 18) % 360;
  root.style.setProperty("--ai-from", `${hNum} ${Math.min(80, sNum)}% ${aiFromL}%`);
  root.style.setProperty("--ai-to",   `${aiToH} ${Math.min(85, sNum + 10)}% ${aiToL}%`);

  // AI hero panel — the dark branded slab behind the AI-insights hero and the
  // user/vessel form headers (`.ai-hero-dark`). Only the HUE and saturation
  // follow the brand; the lightness ladder (11/17/26) is fixed because the
  // panel carries white text in BOTH themes and must stay dark whatever colour
  // the company picks. Saturation is capped so a fully-saturated brand doesn't
  // turn the slab garish. The default #3194A0 reproduces the stylesheet values
  // exactly — 200 34% 11%, 192 46% 17%, 187 56% 26%.
  const heroS = (delta: number) => Math.max(0, Math.min(70, sNum + delta));
  root.style.setProperty("--ai-hero-1", `${(hNum + 13) % 360} ${heroS(-19)}% 11%`);
  root.style.setProperty("--ai-hero-2", `${(hNum + 5) % 360} ${heroS(-7)}% 17%`);
  root.style.setProperty("--ai-hero-3", `${hNum} ${heroS(3)}% 26%`);
  // Light tint for text/icons ON that dark slab (headline highlight, stat-tile
  // icons, token meter). Fixed high lightness so it always reads on the panel.
  root.style.setProperty("--ai-on-dark", `${hNum} ${Math.max(35, Math.min(85, sNum + 19))}% 73%`);

  // Hex copies for any caller that needs a direct hex value (e.g. inline
  // SVG fill on the analytics charts).
  root.style.setProperty("--brand-primary", hex);
}

/** Remove all brand-related inline custom properties from :root.
 *
 *  Why this matters: applyCssVariables() sets inline styles (via
 *  `element.style.setProperty`), which take precedence over the
 *  stylesheet rules in index.css. If we just set the inline value
 *  back to the default hex on logout, the inline override is still
 *  there — masking the stylesheet's `:root { --primary: ... }` rule
 *  and preventing any future theme switching (dark/light mode also
 *  swaps --primary, but only on the stylesheet level).
 *
 *  Removing the inline properties lets the stylesheet defaults take
 *  over cleanly. We call this whenever the user is not authenticated
 *  (login screen, or after logout), so the default #3194A0 is rendered
 *  by the stylesheet rule itself — no JS-set overrides on top of it. */
function clearCssVariables(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const props = [
    "--primary", "--primary-hover", "--primary-active",
    "--primary-fg", "--primary-soft", "--primary-soft-fg",
    "--ring",
    "--ai-from", "--ai-to", "--ai-soft", "--ai-soft-fg", "--ai-border",
    "--ai-hero-1", "--ai-hero-2", "--ai-hero-3", "--ai-on-dark",
    "--sidebar-accent",
    "--brand-primary",
  ];
  for (const p of props) root.style.removeProperty(p);
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState<CompanyBranding>(defaultBranding);

  const fetchBranding = useCallback(async (): Promise<void> => {
    // Not authenticated — log-in screen, or post-logout. Remove any inline
    // brand overrides so the stylesheet's default #3194A0 wins. Without
    // this, a prior session's color leaks into the login screen because
    // inline element.style overrides take priority over :root rules.
    if (!tokens.access) {
      setBranding(defaultBranding);
      clearCssVariables();
      return;
    }
    try {
      const b = await api.one<CompanyBranding>("/api/companies/me/branding");
      setBranding(b);
      // If the company has no override (primaryColor === null), strip any
      // previously-applied inline styles so the default stylesheet rule
      // takes over — same logic as the unauthenticated path. Otherwise
      // apply the override.
      if (b.primaryColor) {
        applyCssVariables(b.primaryColor);
      } else {
        clearCssVariables();
      }
    } catch {
      // Branding fetch failure (network, 401) → defaults. Clear overrides
      // for the same reason as above.
      setBranding(defaultBranding);
      clearCssVariables();
    }
  }, []);

  // Initial load + reload when the auth user changes (login / logout /
  // switching accounts).
  useEffect(() => {
    void fetchBranding();
  }, [user?.id, user?.companyId, fetchBranding]);

  // At first mount (before any auth state has settled), ensure the
  // stylesheet defaults are in effect. The user might be on the login
  // screen with a stale cached session that's about to fail validation.
  useEffect(() => {
    clearCssVariables();
  }, []);

  // The AI accent tokens are theme-aware — they need different
  // lightness in light vs dark mode. applyCssVariables() reads
  // data-theme at write time, so after the user toggles the theme the
  // values stay frozen at the old recipe until something else triggers
  // a re-apply. Watch the data-theme attribute and re-derive on flip.
  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      if (branding.primaryColor) applyCssVariables(branding.primaryColor);
    });
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, [branding.primaryColor]);

  const value = useMemo<BrandingState>(
    () => ({ branding, refresh: fetchBranding }),
    [branding, fetchBranding],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingState {
  return useContext(BrandingContext);
}

/** Build an absolute URL for the logo image. Used by `<img src=...>` in the
 *  sidebar and other branded UI. Returns null when no logo is set. */
export function useLogoUrl(): string | null {
  const { branding } = useBranding();
  // The logoUrl from the API is a relative path (/api/companies/X/logo).
  // We pass it straight to <img>; the Vite dev server's proxy and the
  // bundled axios client both resolve relative /api paths.
  if (!branding.logoUrl) return null;
  // Cache buster: when company updates the logo, branding state is
  // refreshed; appending companyId-based query keeps requests stable
  // until refresh() runs again.
  return `${branding.logoUrl}?v=${branding.companyId ?? 0}`;
}
